import { logEvent } from "@/analytics/analytics"
import { gql } from "@/generated"
import { useCurrentOrganization } from "@/novus/hooks/useCurrentOrganization"
import { useSafeMutation } from "@/util/useSafeMutation"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import invariant from "tiny-invariant"
import { z, ZodType } from "zod"
import { displayValidationErrors } from "@/util/validations"
import { useToast } from "@/components/ui/use-toast"
import { FieldGroup } from "@/components/ui/field-group"
import { RhfField } from "@/components/ui/rhf-field"
import { FormActions } from "@/components/ui/form-actions"
import { Button } from "@/components/ui/button"

const UpdateMembershipMutation = gql(`
  mutation UpdateOrganizationMembership($input: UpdateOrganizationMembershipInput!){
    updateOrganizationMembership(input: $input) {
      organizationMembership {
        ...OrganizationMembership
      }
      errors {
        path
        fullMessages
      }
    }
  }
`)

type FormValues = {
  workEmail: string
}

const schema: ZodType<FormValues> = z.object({
  workEmail: z
    .string()
    .min(1, "Email address is required")
    .email({ message: "Email address is invalid" }),
})

export const WorkEmailForm = ({ onComplete }: { onComplete: () => void }) => {
  const { membership } = useCurrentOrganization()
  const [execUpdateMembership] = useSafeMutation(UpdateMembershipMutation)
  const { toast } = useToast()

  invariant(membership)

  const defaultValues: FormValues = {
    workEmail: membership.workEmail ?? "",
  }

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    logEvent("Submit Work Email")

    try {
      const { data } = await execUpdateMembership({
        variables: {
          input: {
            organizationMembershipId: membership.id,
            workEmail: values.workEmail,
          },
        },
      })

      if (
        data?.updateOrganizationMembership.errors &&
        data?.updateOrganizationMembership.errors.length > 0
      ) {
        throw data?.updateOrganizationMembership.errors
      }

      onComplete()
    } catch (e: any) {
      console.error(e)
      displayValidationErrors({
        recordType: "organizationMembership",
        errors: e,
        setError: form.setError,
        toast,
        validationErrorTitle:
          "There was an error with your provided email address.",
      })
    }
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup className="md:mb-2">
          <RhfField
            name="workEmail"
            control={form.control}
            placeholder="Work Email..."
            label="Work Email Address"
          />
        </FieldGroup>
        <div className="text-sm text-gray-600 mb-4">
          Enter the email your HR Department has on file. If you have any
          questions, ask your HR team or reach out to{" "}
          <a
            href="mailto:support@gohadley.com"
            className="text-blue-600 hover:underline"
          >
            support@gohadley.com
          </a>
          .
        </div>
        <div className="text-sm text-gray-600 mb-4">
          If you don't see the verification code in your inbox, check your spam.
        </div>
        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={form.formState.isSubmitting}
          >
            Verify Email Address
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  )
}
